import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import Swal from 'sweetalert2'
import money from 'v-money3'
import { createApp } from 'vue'
import VueGtag from 'vue-gtag'
import { LoadingPlugin } from 'vue-loading-overlay'
import VueTheMask from 'vue-the-mask'

import ImoAccordion from '@/components/accordion/ImoAccordion.vue'
import ImoAlert from '@/components/alert/ImoAlert.vue'
import ImoAvatar from '@/components/avatar/ImoAvatar.vue'
import ImoBadge from '@/components/badges/ImoBadge.vue'
import ImoButton from '@/components/buttons/ImoButton.vue'
import ImoButtonGroup from '@/components/buttons/ImoButtonGroup.vue'
import ImoTextButton from '@/components/buttons/ImoTextButton.vue'
import ImoContextMenu from '@/components/context-menu/ImoContextMenu.vue'
import ImoDropdown from '@/components/dropdown/ImoDropdown.vue'
import ImoMenuItem from '@/components/dropdown/ImoMenuItem.vue'
import HeroBlock from '@/components/heroblock/HeroBlock.vue'
import ImoIcon from '@/components/icons/ImoIcon.vue'
import ImoSkeleton from '@/components/loading/ImoSkeleton.vue'
import ImoSkeletonCard from '@/components/loading/ImoSkeletonCard.vue'
import ImoSkeletonTable from '@/components/loading/ImoSkeletonTable.vue'
import ImoSkeletonText from '@/components/loading/ImoSkeletonText.vue'
import ImoSpinner from '@/components/loading/ImoSpinner.vue'
import Segment from '@/components/menu/Segment.vue'
import SegmentedControl from '@/components/menu/SegmentedControl.vue'
import ImoModal from '@/components/modal/ImoModal.vue'
import ImoModal_ from '@/components/modal/ImoModal_.vue'
import SidePanel from '@/components/modal/SidePanel.vue'
import NotificationPopUp from '@/components/notification/NotificationPopUp.vue'
import ImoPagination from '@/components/pagination/ImoPagination.vue'
import ImoProgress from '@/components/progress/ImoProgress.vue'
import ImoFilter from '@/components/tables/ImoFilter.vue'
import ImoTable from '@/components/tables/ImoTable.vue'
import ImoTableV2 from '@/components/tables/ImoTableV2.vue'
import ImoTab from '@/components/tabs/ImoTab.vue'
import ImoTabs from '@/components/tabs/ImoTabs.vue'
import clickOutside from '@/plugins/clickOutside'
import ImoTooltip from '@/plugins/ImoTooltip'
import logger from '@/services/logger'
import { initObservability } from '@/services/observability'
import settings from '@/settings/settings'
import { useAuthStore } from '@/stores/Auth'

import App from './App.vue'
import ToastPlugin from './components/toast/ImoToastPlugin.js'
import { router } from './routes'
import { canUnlinkedAdminAccess, isHiddenForLinkedAdmin, resolvePerspective } from './routes/guards'
import {
  setupInterceptors,
  urlNextExceptions,
  generateCleanNextQuery,
} from './services/interceptors'
import { startSession } from './services/session'

// App
const app = createApp(App)

// Pinia
const pinia = createPinia()
pinia.use(piniaPluginPersistedstate)
app.use(pinia)

// Plugins Vue
app.use(router)
app.use(VueTheMask)
app.use(money, { precision: 2 })
app.use(LoadingPlugin)
app.use(ToastPlugin, { maxToasts: 5 })
app.use(
  VueGtag,
  {
    config: {
      id: 'G-8R7EPCMZGB',
      params: {
        send_page_view: false,
      },
    },
  },
  router,
)

// Diretivas
app.directive('tooltip', ImoTooltip)
app.directive('click-outside', clickOutside)

// Componentes globais
app.component('ImoIcon', ImoIcon)
app.component('HeroBlock', HeroBlock)
app.component('ImoButton', ImoButton)
app.component('ImoTextButton', ImoTextButton)
app.component('ImoAlert', ImoAlert)
app.component('ImoAvatar', ImoAvatar)
app.component('ImoBadge', ImoBadge)
app.component('ImoAccordion', ImoAccordion)
app.component('ImoTabs', ImoTabs)
app.component('ImoTab', ImoTab)
app.component('ImoDropdown', ImoDropdown)
app.component('ImoMenuItem', ImoMenuItem)
app.component('ImoContextMenu', ImoContextMenu)
app.component('ImoModal', ImoModal)
app.component('ImoButtonGroup', ImoButtonGroup)
app.component('ImoModal_', ImoModal_)
app.component('SidePanel', SidePanel)
app.component('ImoPagination', ImoPagination)
app.component('ImoFilter', ImoFilter)
app.component('ImoTable', ImoTable)
app.component('ImoTableV2', ImoTableV2)
app.component('ImoProgress', ImoProgress)
app.component('NotificationPopUp', NotificationPopUp)
app.component('SegmentedControl', SegmentedControl)
app.component('Segment', Segment)
app.component('ImoSpinner', ImoSpinner)
app.component('ImoSkeleton', ImoSkeleton)
app.component('ImoSkeletonText', ImoSkeletonText)
app.component('ImoSkeletonCard', ImoSkeletonCard)
app.component('ImoSkeletonTable', ImoSkeletonTable)

// CSS
import('./assets/style/main.css')
import('sweetalert2/dist/sweetalert2.min.css')
import('vue-loading-overlay/dist/css/index.css')

// Sessão: interceptors antes de qualquer HTTP do boot (o /me de reconciliação).
const authStore = useAuthStore()
startSession(authStore, { installInterceptors: setupInterceptors })

// Auth guard
router.beforeEach(async (to, from, next) => {
  if (to.matched.some(record => record.meta.devOnly) && settings.ENV === 'prod') {
    return next({ name: 'Error' })
  }

  const requiresAuth = to.matched.some(record => record.meta.auth)
  const requiresAdmin = to.matched.some(record => record.meta.admin)

  if (requiresAuth) {
    if (!authStore.isLoggedIn) {
      return next({ path: '/login', query: { next: to.fullPath } })
    }
    if (requiresAdmin && !authStore.isSuperuser) {
      return next({ path: '/dashboard' })
    }
    if (authStore.isLinkedAdmin) {
      if (isHiddenForLinkedAdmin(to)) {
        return next({ path: '/admin/painel' })
      }
    }
    // Admin sem vínculo não navega para área de cliente: as telas fazem requests
    // sem o header Client e quebram (ex.: extrato lê authData.link.digital_account).
    // Rotas liberadas para esse caso marcam meta.adminUnlinked: true.
    if (authStore.isUnlinkedAdmin && !requiresAdmin && !canUnlinkedAdminAccess(to)) {
      return next({ path: '/admin' })
    }
    if (!authStore.isValidated && !authStore.isSuperuser) {
      try {
        Swal.fire({
          title: 'Atenção!',
          text: 'Entre em contato com o suporte.',
          icon: 'info',
        })
        await authStore.logout()
        return next({ path: '/login' })
      } catch (err) {
        logger.error('Failed to logout:', err)
        return next(false)
      }
    }
  }

  const exceptionRedirect = urlNextExceptions.some(exception => to.query.next?.includes(exception))

  if (exceptionRedirect) {
    to.query.next = generateCleanNextQuery(to.query.next)
    return next(to)
  }

  return next()
})

// A perspectiva só muda com a navegação confirmada: um guard posterior que cancele
// ou redirecione não pode deixar o layout na visão da rota que não abriu.
router.afterEach((to, from, failure) => {
  if (failure || !authStore.isLinkedAdmin) return
  if (!to.matched.some(record => record.meta.auth)) return
  authStore.setPerspective(resolvePerspective(to, authStore.perspective))
})

initObservability().catch(error => logger.warn('[observability] falha ao iniciar o RUM', error))

// Error handler global
app.config.errorHandler = (err, vm, info) => {
  logger.capture(err, { source: 'vue-error-handler', info })
}

app.mount('#app')
